import { useState, useCallback } from "react";
import { useI18n } from "./i18n";

export type Difficulty = "easy" | "normal" | "hard";

const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  easy: "easy",
  normal: "normal",
  hard: "hard",
};

/**
 * In-game settings panel. Driven entirely by facade calls where possible.
 *
 * - Sound: UI-only toggle; no engine-side effect yet
 * - Difficulty: selects a map variant; stored in localStorage for persistence
 * - Language: toggle between zh/en, persisted in localStorage
 */
export interface SettingsPanelProps {
  difficulty: Difficulty;
  onDifficultyChange: (d: Difficulty) => void;
  onClose: () => void;
  soundEnabled: boolean;
  onSoundToggle: () => void;
  onEncyclopediaOpen: () => void;
  onDamageMatrixOpen: () => void;
}

export function SettingsPanel({
  difficulty,
  onDifficultyChange,
  onClose,
  soundEnabled,
  onSoundToggle,
  onEncyclopediaOpen,
  onDamageMatrixOpen,
}: SettingsPanelProps) {
  const [soundSaved, setSoundSaved] = useState(false);
  const { t, locale, setLocale } = useI18n();

  const handleSoundToggle = useCallback(() => {
    onSoundToggle();
    setSoundSaved(true);
  }, [onSoundToggle]);

  const handleDifficulty = useCallback(
    (d: Difficulty) => {
      onDifficultyChange(d);
      localStorage.setItem("bastion-difficulty", d);
    },
    [onDifficultyChange],
  );

  const difficultyDescription =
    difficulty === "easy"
      ? t("difficulty_easy_desc")
      : difficulty === "hard"
        ? t("difficulty_hard_desc")
        : t("difficulty_normal_desc");

  return (
    <div className="settings-overlay" data-testid="settings-overlay">
      <div className="settings-panel" data-testid="settings-panel">
        <div className="settings-header">
          <h2 className="settings-title">{t("settings_title")}</h2>
          <div className="settings-header-actions">
            <button className="settings-close" data-testid="settings-close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* 图鉴与伤害表 */}
        <section className="settings-section">
          <div className="settings-reference-actions">
            <button
              className="settings-sound-btn enabled"
              data-testid="settings-encyclopedia-open"
              onClick={onEncyclopediaOpen}
            >
              {t("hud_encyclopedia")}
            </button>
            <button
              className="settings-sound-btn enabled"
              data-testid="settings-damage-matrix-open"
              onClick={onDamageMatrixOpen}
            >
              {t("encyclopedia_damage_matrix_btn")}
            </button>
          </div>
        </section>

        {/* Sound */}
        <section className="settings-section">
          <h3 className="settings-section-title">{t("settings_sound")}</h3>
          <button
            className={`settings-sound-btn ${soundEnabled ? "enabled" : "disabled"}`}
            data-testid="sound-toggle"
            onClick={handleSoundToggle}
          >
            {soundEnabled ? t("hud_sound_on") : t("hud_sound_off")}
          </button>
          {soundSaved && (
            <p className="settings-saved-hint" data-testid="settings-saved-hint">
              {t("settings_saved")}
            </p>
          )}
        </section>

        {/* Difficulty */}
        <section className="settings-section">
          <h3 className="settings-section-title">{t("settings_difficulty")}</h3>
          <div className="settings-difficulty-list">
            {(Object.keys(DIFFICULTY_LABELS) as Difficulty[]).map((d) => (
              <button
                key={d}
                className={`settings-difficulty-btn ${difficulty === d ? "active" : ""}`}
                data-testid={`difficulty-${d}-btn`}
                onClick={() => handleDifficulty(d)}
              >
                {t(`difficulty_${d}` as keyof typeof t)}
              </button>
            ))}
          </div>
          <p className="settings-hint">{difficultyDescription}</p>
        </section>

        {/* Language */}
        <section className="settings-section">
          <h3 className="settings-section-title">{t("settings_language")}</h3>
          <div className="settings-difficulty-list">
            <button
              className={`settings-difficulty-btn ${locale === "zh" ? "active" : ""}`}
              data-testid="language-zh-btn"
              onClick={() => setLocale("zh")}
            >
              {t("settings_language_zh")}
            </button>
            <button
              className={`settings-difficulty-btn ${locale === "en" ? "active" : ""}`}
              data-testid="language-en-btn"
              onClick={() => setLocale("en")}
            >
              {t("settings_language_en")}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
